import React, { useEffect, useState, useMemo } from "react";
import { ErrorM } from "components/helpers/FormHelpers/ErrorM";
import { debounce } from "lodash";
import { useFormikContext, useField } from "formik";
import { FormField } from "@avamae/formbuilder";
import editIcon from "assets/edit_icon.png";

export interface FormCurrencyFieldProps<T> {
    fieldName: string;
    label: string;
    readOnly?: boolean;
    viewOnly?: boolean;
    hideEditIcon?: boolean;
}

const CurrencyField = function <T>({
    fieldName,
    label,
    readOnly,
    viewOnly,
    hideEditIcon,
}: FormCurrencyFieldProps<T>) {
    const [field] = useField(fieldName as string);
    const formik = useFormikContext();
    const [inputValue, setInputValue] = useState<number | null | string>(null);
    const [relevantFormikError, setRelevantFormikError] = useState<string | null>(null);

    const formikErrors = formik.errors;

    useEffect(() => {
        if (formikErrors.hasOwnProperty(field.name)) {
            const error = formikErrors[field.name as keyof typeof formikErrors];

            if (typeof error === "string") {
                setRelevantFormikError(error);
            }
        } else {
            setRelevantFormikError(null);
        }
    }, [formikErrors]);

    // Set the initial value of the input field on load
    useEffect(() => {
        formatInput(field.value);
    }, []);

    // Update the input field value when the formik field value changes
    // Needed because the formik field value can change by other means than the input field
    useEffect(() => {
        formatInput(field.value);
    }, [field.value]);

    // Updates the field immediately when the user types
    // Formats the input value to a number with 2 decimal places after the user stops typing
    const handleImmediateChange = (value: string) => {
        setInputValue(value);
        debouncedFormat(value);
    };
    const debouncedFormat = useMemo(
        () =>
            debounce((value: string | number | null) => {
                formatInput(value);
            }, 3000),
        []
    );

    // Formats the input value to a number with 2 decimal places and updates the formik field value
    const formatInput = (value: string | number | null) => {
        let parsedValue: number | null = null;

        if (value !== null) {
            const num = typeof value === "string" ? parseFloat(value) : value;
            if (!isNaN(num)) {
                parsedValue = parseFloat(num.toFixed(2));
            }
        }

        setInputValue(parsedValue !== null ? parsedValue.toFixed(2) : "");
        formik.setFieldValue(field.name, parsedValue);
    };

    return (
        <div className={`FormBox FormSideBySide`}>
            <div className="FormLabel">
                <label>
                    {`${label}`}
                    {!readOnly && !hideEditIcon && (
                        <img src={editIcon} alt="edit icon" className="EditIcon" />
                    )}
                </label>
            </div>
            <div className="FormField">
                <React.Fragment>
                    <input
                        className={`EditBox ${viewOnly ? "ViewOnly" : readOnly ? " CurrencyReadOnly" : ""} currencyInputField`}
                        value={
                            readOnly ? (inputValue ? inputValue : "") : inputValue ?? ""
                        }
                        type={readOnly ? "text" : "number"}
                        step="any"
                        readOnly={readOnly}
                        onChange={e => handleImmediateChange(e.target.value)}
                        onBlur={e => formatInput(e.target.value)}
                    />
                    <ErrorM name={fieldName as string} />
                </React.Fragment>
            </div>
        </div>
    );
};

export const currencyRenderer = (
    readOnly: boolean | undefined,
    viewOnly: boolean | undefined,
    hideEditIcon?: boolean | undefined
) =>
    function currencyRenderer<T>(_key: React.ReactText, field: FormField<T>) {
        return (
            <CurrencyField
                fieldName={field.name as string}
                label={field.label as string}
                readOnly={readOnly ?? false}
                viewOnly={viewOnly ?? false}
                hideEditIcon={hideEditIcon ?? false}
            />
        );
    }; 